// src/pages/evenements/assistances/ListeAssistances.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Spinner, Modal, Alert, Button, Table, Pagination, Form, InputGroup, Badge } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Header from '../../../layouts/Header';
import Footer from '../../../layouts/Footer';
import { ROLES } from '../../../data/Menu';
import { COLORS } from '../../../constants/colors';

// Fonctions utilitaires
const truncateText = (text, maxLength = 25) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const formatDateSimple = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const getTypeColor = (type) => {
    const typeLower = type?.toLowerCase() || '';
    if (typeLower.includes('culte')) return COLORS.purple;
    if (typeLower.includes('réunion') || typeLower.includes('reunion')) return COLORS.info;
    if (typeLower.includes('enseignement')) return COLORS.success;
    if (typeLower.includes('prière') || typeLower.includes('priere')) return COLORS.rose;
    if (typeLower.includes('jeûne') || typeLower.includes('jeune')) return COLORS.warning;
    return COLORS.primary;
};

export default function ListeAssistances() {
    const navigate = useNavigate();
    const location = useLocation();

    // États
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [egliseFilter, setEgliseFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [eglises, setEglises] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [assistanceToDelete, setAssistanceToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [stats, setStats] = useState({
        total: 0,
        totalParticipants: 0,
        totalHommes: 0,
        totalFemmes: 0,
        totalEnfants: 0
    });
    const [permissions, setPermissions] = useState({
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
        canFilter: false
    });

    // ===== Permissions =====
    const hasPermission = (action) => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return false;
            const user = JSON.parse(userStr);
            const userRole = user.role || user.type_utilisateur;
            if (userRole === ROLES.SUPER_ADMIN) return true;

            const permissionsMap = {
                'view_evenements': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL, ROLES.NOUVEAU, ROLES.MEMBRE],
                'add_evenement': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL],
                'edit_evenement': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL],
                'delete_evenement': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR],
                'export_evenements': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL],
                'filter_evenements': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL, ROLES.NOUVEAU, ROLES.MEMBRE]
            };
            return permissionsMap[action]?.includes(userRole) || false;
        } catch (e) {
            console.error('Erreur permission:', e);
            return false;
        }
    };

    useEffect(() => {
        setPermissions({
            canView: hasPermission('view_evenements'),
            canAdd: hasPermission('add_evenement'),
            canEdit: hasPermission('edit_evenement'),
            canDelete: hasPermission('delete_evenement'),
            canExport: hasPermission('export_evenements'),
            canFilter: hasPermission('filter_evenements')
        });
    }, []);

    // ===== API =====
    const fetchEglises = useCallback(async () => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return;
            const user = JSON.parse(userStr);
            const userId = user.id || user.id_utilisateur;
            const response = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}&type=siege&limit=1000`, {
                headers: { 'Accept': 'application/json' }
            });
            const result = await response.json();
            if (result.success) {
                setEglises(result.eglises || []);
            }
        } catch (err) {
            console.error('Erreur chargement églises:', err);
        }
    }, []);

const fetchAssistances = useCallback(async () => {
    if (data) setFilterLoading(true);
    else setLoading(true);

    try {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
            navigate('/pages/signin2');
            return;
        }
        const user = JSON.parse(userStr);
        const userId = user.id || user.id_utilisateur;

        const timestamp = Date.now();
        let url = `https://josephsbusiness.com/api/assistances.php?user_id=${userId}&page=${currentPage}&limit=${itemsPerPage}&_=${timestamp}`;
        if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
        if (egliseFilter) url += `&eglise=${encodeURIComponent(egliseFilter)}`;
        if (typeFilter) url += `&type=${encodeURIComponent(typeFilter)}`;

        const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
        const result = await response.json();

        if (result.success) {
            setData(result);
            if (result.assistances) {
                let totalHommes = 0, totalFemmes = 0, totalEnfants = 0;
                result.assistances.forEach(assistance => {
                    totalHommes += parseInt(assistance.nb_homme) || 0;
                    totalFemmes += parseInt(assistance.nb_femme) || 0;
                    totalEnfants += parseInt(assistance.nb_enfant) || 0;
                });
                setStats({
                    total: result.pagination?.total || 0,
                    totalParticipants: totalHommes + totalFemmes + totalEnfants,
                    totalHommes,
                    totalFemmes,
                    totalEnfants
                });
            }
        } else {
            setError(result.message || 'Erreur de chargement');
        }
    } catch (err) {
        console.error('Erreur:', err);
        setError(err.message);
    } finally {
        setLoading(false);
        setFilterLoading(false);
    }
}, [currentPage, searchTerm, egliseFilter, typeFilter, itemsPerPage, navigate]); // <- data retiré
    useEffect(() => {
        fetchEglises();
    }, [fetchEglises]);

    useEffect(() => {
        fetchAssistances();
    }, [fetchAssistances]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const reload = params.get('reload');
        if (reload) {
            fetchAssistances();
            navigate('/evenements/assistances', { replace: true });
        }
    }, [location, navigate, fetchAssistances]);

    useEffect(() => {
        if (data?.pagination?.current_page && data.pagination.current_page !== currentPage) {
            setCurrentPage(data.pagination.current_page);
        }
    }, [data?.pagination?.current_page]);

    // ===== Handlers =====
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteClick = (assistance) => {
        setAssistanceToDelete(assistance);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!assistanceToDelete) return;
        setDeleting(true);
        try {
            const userStr = localStorage.getItem('user');
            const user = JSON.parse(userStr);
            const userId = user.id || user.id_utilisateur;
            const response = await fetch(`https://josephsbusiness.com/api/assistances.php?user_id=${userId}&id=${assistanceToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Accept': 'application/json' }
            });
            const result = await response.json();
            if (result.success) {
                setShowDeleteModal(false);
                setAssistanceToDelete(null);
                await fetchAssistances();
                setSuccessMessage('Assistance supprimée avec succès');
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 3000);
            } else {
                alert(result.message || 'Erreur lors de la suppression');
            }
        } catch (err) {
            alert('Erreur: ' + err.message);
        } finally {
            setDeleting(false);
        }
    };

    const exportToExcel = () => {
        if (!data?.assistances) return;
        const fileName = `assistances_${new Date().toISOString().split('T')[0]}.xlsx`;
        const exportData = data.assistances.map(item => ({
            'Matricule': item.matricule,
            'Thème': item.theme,
            'Type': item.type,
            'Orateur': item.orateur,
            'Date': formatDateSimple(item.date_at),
            'Hommes': item.nb_homme,
            'Femmes': item.nb_femme,
            'Enfants': item.nb_enfant,
            'Total participants': (parseInt(item.nb_homme) + parseInt(item.nb_femme) + parseInt(item.nb_enfant)),
            'Église': item.eglise_nom
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.sheet_add_aoa(ws, [
            ['LISTE DES ASSISTANCES'],
            [`Généré le ${new Date().toLocaleDateString('fr-FR')}`],
            [`Total: ${stats.total} assistances`],
            [`Participants: ${stats.totalParticipants} personnes`],
            []
        ], { origin: 'A1' });
        XLSX.utils.book_append_sheet(wb, ws, "Assistances");
        XLSX.writeFile(wb, fileName);
    };

    const printPDF = () => {
        const printWindow = window.open('', '_blank');
        const assistances = data?.assistances || [];
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Liste des assistances</title>
                <meta charset="UTF-8">
                <style>
                    body { font-family: Arial; margin: 30px; }
                    .header { text-align: center; border-bottom: 2px solid #295AA7; margin-bottom: 30px; }
                    h1 { color: #295AA7; }
                    .summary { background: #f8fafc; padding: 20px; border-left: 4px solid #FDB52F; margin-bottom: 20px; }
                    table { width: 100%; border-collapse: collapse; font-size: 11px; }
                    th { background: #295AA7; color: white; padding: 8px; text-align: left; }
                    td { padding: 6px 8px; border: 1px solid #ddd; }
                    tr:nth-child(even) { background: #f8fafc; }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>Liste des assistances</h1>
                    <p>Généré le ${new Date().toLocaleDateString('fr-FR')}</p>
                </div>
                <div class="summary">
                    <p><strong>Total assistances:</strong> ${stats.total}</p>
                    <p><strong>Total participants:</strong> ${stats.totalParticipants}</p>
                    <p><strong>Hommes:</strong> ${stats.totalHommes} | <strong>Femmes:</strong> ${stats.totalFemmes} | <strong>Enfants:</strong> ${stats.totalEnfants}</p>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Thème</th>
                            <th>Type</th>
                            <th>Orateur</th>
                            <th>H</th>
                            <th>F</th>
                            <th>E</th>
                            <th>Total</th>
                            <th>Église</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${assistances.map(a => {
                            const total = (parseInt(a.nb_homme) + parseInt(a.nb_femme) + parseInt(a.nb_enfant));
                            return `
                                <tr>
                                    <td>${formatDateSimple(a.date_at)}</td>
                                    <td>${a.theme.substring(0, 20)}${a.theme.length > 20 ? '...' : ''}</td>
                                    <td>${a.type.substring(0, 10)}${a.type.length > 10 ? '...' : ''}</td>
                                    <td>${a.orateur.substring(0, 15)}${a.orateur.length > 15 ? '...' : ''}</td>
                                    <td>${a.nb_homme}</td>
                                    <td>${a.nb_femme}</td>
                                    <td>${a.nb_enfant}</td>
                                    <td><strong>${total}</strong></td>
                                    <td>${a.eglise_nom.substring(0, 15)}${a.eglise_nom.length > 15 ? '...' : ''}</td>
                                </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;
        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
    };

    // ===== Loading =====
    if (loading && !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
                            <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des assistances</h5>
                            <p className="text-muted">Récupération des données en cours...</p>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error && !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchAssistances}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const assistances = data?.assistances || [];
    const pagination = data?.pagination || { total: 0, pages: 1 };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    {/* ===== EN‑TÊTE MODERNE (comme Membres) ===== */}
                    <div className="header-modern mb-4 fade-in">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-calendar-line"></i>
                            </div>
                            <div>
                                <h5 className="gradient-text">Assistances</h5>
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {stats.total}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-group-line"></i> Participants {stats.totalParticipants}
                                    </span>
                                   
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            {permissions.canFilter && (
                                <Button size="sm" className="btn-modern-outline" onClick={() => setShowFilters(!showFilters)}>
                                    <i className="ri-filter-line"></i> {showFilters ? 'Masquer' : 'Filtres'}
                                </Button>
                            )}
                            {permissions.canExport && (
                                <>
                                    <Button size="sm" className="btn-modern-outline" onClick={exportToExcel}>
                                        <i className="ri-file-excel-line"></i> Excel
                                    </Button>
                                    <Button size="sm" className="btn-modern-outline" onClick={printPDF}>
                                        <i className="ri-file-pdf-line"></i> PDF
                                    </Button>
                                </>
                            )}
                            {permissions.canAdd && (
                                <Button size="sm" className="btn-modern-primary" onClick={() => navigate('/evenements/assistances/nouveau')}>
                                    <i className="ri-add-line"></i> Nouvelle
                                </Button>
                            )}
                            <Button size="sm" className="btn-modern-outline" onClick={fetchAssistances} disabled={filterLoading}>
                                <i className={`ri-refresh-line ${filterLoading ? 'fa-spin' : ''}`}></i> Actualiser
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS (comme Membres) ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="2">
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon"><i className="ri-calendar-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total</span>
                                    <h3 className="kpi-value">{stats.total}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="2">
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon"><i className="ri-group-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Participants</span>
                                    <h3 className="kpi-value">{stats.totalParticipants}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="2">
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon"><i className="ri-men-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Hommes</span>
                                    <h3 className="kpi-value">{stats.totalHommes}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="2">
                            <div className="kpi-card" style={{ borderLeftColor: COLORS.pink }}>
                                <div className="kpi-icon" style={{ background: 'rgba(236,72,153,0.1)', color: COLORS.pink }}>
                                    <i className="ri-women-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Femmes</span>
                                    <h3 className="kpi-value">{stats.totalFemmes}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="2">
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon"><i className="ri-group-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Enfants</span>
                                    <h3 className="kpi-value">{stats.totalEnfants}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="2">
                            <div className="kpi-card" style={{ borderLeftColor: COLORS.purple }}>
                                <div className="kpi-icon" style={{ background: 'rgba(139,92,246,0.1)', color: COLORS.purple }}>
                                    <i className="ri-bar-chart-2-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Églises</span>
                                    <h3 className="kpi-value">{data?.eglises_count || 0}</h3>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== FILTRES (fond primaryDark, comme Membres) ===== */}
                    {permissions.canFilter && showFilters && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4" style={{ background: COLORS.primaryDark }}>
                            <Card.Body className="py-3 px-4">
                                <Form onSubmit={handleSearch}>
                                    <Row className="align-items-center g-3">
                                        <Col md="4">
                                            <div className="position-relative">
                                                <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-white-50 fs-14"></i>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Thème, orateur..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="ps-5 rounded-pill"
                                                    style={{ background: 'rgba(255,255,255,0.9)' }}
                                                />
                                            </div>
                                        </Col>
                                        <Col md="3">
                                            <Form.Select
                                                value={egliseFilter}
                                                onChange={(e) => setEgliseFilter(e.target.value)}
                                                className="rounded-pill px-3"
                                                style={{ background: 'rgba(255,255,255,0.9)' }}
                                            >
                                                <option value="">Toutes les églises</option>
                                                {eglises.map(eglise => (
                                                    <option key={eglise.matricule} value={eglise.matricule}>
                                                        {truncateText(eglise.nom, 20)}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col md="3">
                                            <Form.Select
                                                value={typeFilter}
                                                onChange={(e) => setTypeFilter(e.target.value)}
                                                className="rounded-pill px-3"
                                                style={{ background: 'rgba(255,255,255,0.9)' }}
                                            >
                                                <option value="">Tous les types</option>
                                                <option value="culte">Culte</option>
                                                <option value="reunion">Réunion</option>
                                                <option value="enseignement">Enseignement</option>
                                                <option value="priere">Prière</option>
                                                <option value="jeune">Jeûne</option>
                                            </Form.Select>
                                        </Col>
                                        <Col md="2">
                                            <Button
                                                type="submit"
                                                variant="light"
                                                size="sm"
                                                className="rounded-pill w-100"
                                                disabled={filterLoading}
                                            >
                                                {filterLoading ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== MESSAGE DE SUCCÈS ===== */}
                    {showSuccessMessage && (
                        <Alert
                            variant="success"
                            className="border-0 shadow-sm rounded-3 mb-3"
                            onClose={() => setShowSuccessMessage(false)}
                            dismissible
                        >
                            <div className="d-flex align-items-center">
                                <i className="ri-checkbox-circle-line fs-4 me-2" style={{ color: COLORS.success }}></i>
                                <span>{successMessage}</span>
                            </div>
                        </Alert>
                    )}

                    {/* ===== TABLEAU (en‑tête secondary) ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="border-0 py-3" style={{ background: COLORS.secondary }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <i className="ri-list-check text-white me-2 fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des assistances</span>
                                    <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                                        {pagination.total} au total
                                    </Badge>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Select
                                        size="sm"
                                        style={{ width: 'auto', background: 'rgba(255,255,255,0.9)' }}
                                        value={itemsPerPage}
                                        onChange={handleItemsPerPageChange}
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                    </Form.Select>
                                    {filterLoading && <Spinner animation="border" size="sm" variant="light" />}
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Date</th>
                                            <th>Thème</th>
                                            <th>Type</th>
                                            <th>Orateur</th>
                                            <th className="text-center">Participants</th>
                                            <th>Église</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assistances.length > 0 ? (
                                            assistances.map((assistance) => {
                                                const total = (parseInt(assistance.nb_homme) + parseInt(assistance.nb_femme) + parseInt(assistance.nb_enfant));
                                                const typeColor = getTypeColor(assistance.type);
                                                return (
                                                    <tr key={assistance.id}>
                                                        <td className="ps-4">
                                                            <span className="reference-badge">{formatDateSimple(assistance.date_at)}</span>
                                                        </td>
                                                        <td>{truncateText(assistance.theme, 20)}</td>
                                                        <td>
                                                            <span
                                                                className="badge rounded-pill"
                                                                style={{
                                                                    backgroundColor: typeColor,
                                                                    color: 'white',
                                                                    padding: '0.25rem 0.6rem',
                                                                    fontSize: '0.7rem'
                                                                }}
                                                            >
                                                                {truncateText(assistance.type, 10)}
                                                            </span>
                                                        </td>
                                                        <td>{truncateText(assistance.orateur, 15)}</td>
                                                        <td className="text-center">
                                                            <span className="fw-bold" style={{ color: COLORS.primary }}>{total}</span>
                                                            <small className="text-muted d-block" style={{ fontSize: '0.6rem' }}>
                                                                H:{assistance.nb_homme} F:{assistance.nb_femme} E:{assistance.nb_enfant}
                                                            </small>
                                                        </td>
                                                        <td>{truncateText(assistance.eglise_nom, 15)}</td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                {permissions.canEdit && (
                                                                    <button
                                                                        className="action-icon action-icon-primary"
                                                                        onClick={() => navigate(`/evenements/assistances/modifier/${assistance.id}`)}
                                                                        title="Modifier"
                                                                    >
                                                                        <i className="ri-pencil-line"></i>
                                                                    </button>
                                                                )}
                                                                {permissions.canDelete && (
                                                                    <button
                                                                        className="action-icon action-icon-danger"
                                                                        onClick={() => handleDeleteClick(assistance)}
                                                                        title="Supprimer"
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h6 className="mb-1">Aucune assistance trouvée</h6>
                                                    <p className="text-muted small mb-0">Essayez de modifier vos critères de recherche</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {pagination.pages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4 px-3">
                            <div className="text-muted small">
                                Page {currentPage} sur {pagination.pages}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                                    let pageNum;
                                    if (pagination.pages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= pagination.pages - 2) pageNum = pagination.pages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => handlePageChange(pageNum)}>
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.pages} />
                                <Pagination.Last onClick={() => handlePageChange(pagination.pages)} disabled={currentPage === pagination.pages} />
                            </Pagination>
                        </div>
                    )}

                    <Footer />
                </div>
            </div>

            {/* ===== MODAL DE SUPPRESSION (comme Membres) ===== */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
                className="delete-modal-modern"
            >
                <Modal.Header
                    closeButton
                    className="border-0 pb-0"
                    style={{
                        background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
                        borderTopLeftRadius: '4px',
                        borderTopRightRadius: '4px'
                    }}
                >
                    <Modal.Title className="w-100 text-center">
                        <div className="delete-icon-wrapper mb-2">
                            <div className="delete-icon-circle" style={{
                                width: '70px',
                                height: '70px',
                                borderRadius: '4px',
                                background: 'rgba(255,255,255,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto'
                            }}>
                                <i className="ri-delete-bin-line" style={{ fontSize: '32px', color: 'white' }}></i>
                            </div>
                        </div>
                        <h5 className="fw-bold text-white mb-0">Confirmer la suppression</h5>
                        <p className="text-white-50 small mb-0">Cette action est irréversible</p>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-4 px-4">
                    {assistanceToDelete && (
                        <>
                            <p className="text-center text-muted mb-3">
                                Êtes-vous sûr de vouloir supprimer définitivement cette assistance ?
                            </p>
                            <div className="p-3 mb-3" style={{
                                background: '#f8fafc',
                                borderLeft: `3px solid ${COLORS.primary}`,
                                borderRadius: '4px'
                            }}>
                                <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                    {truncateText(assistanceToDelete.theme, 40)}
                                </div>
                                <div className="text-muted small">
                                    <i className="ri-calendar-line me-1"></i>
                                    {formatDate(assistanceToDelete.date_at)}
                                </div>
                                <span
                                    className="badge rounded-pill mt-1"
                                    style={{
                                        backgroundColor: getTypeColor(assistanceToDelete.type),
                                        color: 'white'
                                    }}
                                >
                                    {assistanceToDelete.type}
                                </span>
                            </div>
                            <div style={{
                                background: `${COLORS.danger}10`,
                                border: `1px solid ${COLORS.danger}30`,
                                borderRadius: '4px',
                                padding: '0.8rem 1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.8rem'
                            }}>
                                <i className="ri-alert-line" style={{ color: COLORS.danger, fontSize: '1.3rem' }}></i>
                                <span className="small text-muted">
                                    Toutes les données associées à cette assistance seront définitivement supprimées.
                                </span>
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
                    <Button
                        variant="light"
                        onClick={() => setShowDeleteModal(false)}
                        disabled={deleting}
                        className="px-4"
                        style={{
                            borderRadius: '4px',
                            padding: '0.5rem 1.5rem',
                            border: '1px solid #d1d5db',
                            color: '#4b5563',
                            fontWeight: '500',
                            fontSize: '0.85rem',
                            transition: 'all 0.2s',
                            background: 'white'
                        }}
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteConfirm}
                        disabled={deleting}
                        className="px-4"
                        style={{
                            background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.5rem 1.5rem',
                            fontWeight: '500',
                            fontSize: '0.85rem',
                            boxShadow: '0 4px 10px rgba(239, 68, 68, 0.25)',
                            transition: 'all 0.2s',
                            color: 'white'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 16px rgba(239, 68, 68, 0.35)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 10px rgba(239, 68, 68, 0.25)';
                        }}
                    >
                        {deleting ? (
                            <><Spinner size="sm" className="me-1" /> Suppression...</>
                        ) : (
                            <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES (KPI + actions, identiques à Membres) ===== */}
            <style>{`
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: ${COLORS.primary}; }
                .kpi-success { border-left-color: ${COLORS.success}; }
                .kpi-danger { border-left-color: ${COLORS.danger}; }
                .kpi-warning { border-left-color: ${COLORS.warning}; }
                .kpi-info { border-left-color: ${COLORS.info}; }

                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: rgba(41, 90, 167, 0.1); color: ${COLORS.primary}; }
                .kpi-success .kpi-icon { background: rgba(16, 185, 129, 0.1); color: ${COLORS.success}; }
                .kpi-danger .kpi-icon { background: rgba(239, 68, 68, 0.1); color: ${COLORS.danger}; }
                .kpi-warning .kpi-icon { background: rgba(245, 158, 11, 0.1); color: ${COLORS.warning}; }
                .kpi-info .kpi-icon { background: rgba(59, 130, 246, 0.1); color: ${COLORS.info}; }

                .kpi-content { flex: 1; }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }

                .action-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    color: #6c757d;
                    transition: all 0.2s;
                    cursor: pointer;
                }
                .action-icon:hover {
                    background: ${COLORS.primary};
                    border-color: ${COLORS.primary};
                    color: white !important;
                }
                .action-icon-primary:hover {
                    background: ${COLORS.primary};
                    border-color: ${COLORS.primary};
                    color: white !important;
                }
                .action-icon-danger:hover {
                    background: #dc3545;
                    border-color: #dc3545;
                    color: white !important;
                }

                .reference-badge {
                    background: #f8f9fa;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                }

                .fs-24 { font-size: 1.5rem; }
                .fs-48 { font-size: 3rem; }
                .fs-14 { font-size: 0.875rem; }

                .rounded-3 { border-radius: 0px !important; }
                .rounded-pill { border-radius: 50px !important; }

                .header-modern {
                    background: white;
                    padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid ${COLORS.secondary};
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                }
                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }
                .header-icon-wrapper {
                    width: 48px;
                    height: 48px;
                    background: ${COLORS.primary};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    color: white;
                    font-size: 1.4rem;
                }
                .gradient-text {
                    color: #1e293b;
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0;
                }
                .badge-modern {
                    background: #f1f4f9;
                    color: #475569;
                    padding: 0.3rem 0.8rem;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .badge-modern i {
                    color: ${COLORS.secondary};
                }
                .btn-modern-outline {
                    border: 1px solid #d1d5db;
                    background: white;
                    padding: 0.4rem 1.2rem;
                    border-radius: 4px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: #4b5563;
                    transition: all 0.2s;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: ${COLORS.secondary};
                    color: ${COLORS.secondary};
                    background: #fafafa;
                }
                .btn-modern-primary {
                    background: ${COLORS.primary};
                    border: none;
                    padding: 0.4rem 1.2rem;
                    border-radius: 4px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: white;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    transition: all 0.2s;
                }
                .btn-modern-primary:hover {
                    background: ${COLORS.primaryDark};
                    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
                }
                .fade-in {
                    animation: fadeIn 0.3s ease-out;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(6px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .delete-modal-modern .modal-content {
                    border-radius: 4px;
                    border: none;
                }
            `}</style>
        </>
    );
}